import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { CREATED_NOTICE_MS } from '../quotes/PlacementNotice';
import { QUOTES_KEY } from '../quotes/types';
import { rentalsKey } from '../subrentals/types';
import { DialogLayer } from '../ui/Dialog';
import { packlistKey } from './types';
import type { PlacedSupplierLine } from './versions';

/**
 * Owner 2026-10-24 20:36 („a co pozmieniales ze nie moge usunac dopozyczonej pozycji ze sprzetowki?”): the × of a
 * supplier's line in the packlist. The packlist's own position: „Usunąć „<nazwa>” i dopożyczenie od <dostawca>?” — its
 * rental goes with it (one already picked up: „zmień w Dopożyczeniach”). A quote's line: „tylko ze sprzętówki” (the
 * quote and its rental stay; then „Ukryto … · Cofnij”) or „ze sprzętówki i kosztorysu” (the line of the version the
 * packlist and the rental follow; otherwise disabled with the reason) — what the person may is asked of the server.
 */
type Options = { fromQuote: boolean; mayRemove: boolean; mayHide: boolean; mayDeleteFromQuote: boolean; note?: string; quoteNote?: string };
/** A line just hidden „tylko ze sprzętówki”: its name and the id its „Cofnij” is sent with. */
export type HiddenLine = { name: string; undoId: string };

const urlOf = (eventId: string, id: string) => `/api/quotes/events/${eventId}/packlist-supplier-lines/${id}`;

async function refresh(queryClient: ReturnType<typeof useQueryClient>, eventId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }),
    queryClient.invalidateQueries({ queryKey: rentalsKey(eventId) }),
    queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  ]);
}

export function SupplierRemoveDialog({ eventId, placed, onClose, onHidden }: { eventId: string; placed: PlacedSupplierLine; onClose: () => void; onHidden: (hidden: HiddenLine) => void }) {
  const queryClient = useQueryClient();
  const { line, supplierName } = placed;
  const id = line.id!;
  const fromQuote = !!line.quoteLineId;
  const options = useQuery({ queryKey: ['packlist-supplier-remove', eventId, id], queryFn: () => api<Options>(urlOf(eventId, id)), retry: false });
  const remove = useMutation({
    mutationFn: (from: 'packlist' | 'quote' | null) => api<{ undoId?: string } | undefined>(`${urlOf(eventId, id)}${from ? `?from=${from}` : ''}`, { method: 'DELETE' }),
    onSuccess: async (result) => {
      onClose();
      if (result?.undoId) onHidden({ name: line.name, undoId: result.undoId });
      await refresh(queryClient, eventId);
    },
  });
  const error = remove.error instanceof ApiError ? remove.error.message : remove.error ? 'Nie udało się usunąć pozycji' : '';
  const busy = remove.isPending;
  const o = options.data;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="remove-supplier-title" data-feature-id="packlists.suppliers">
        <DialogLayer onClose={onClose} busy={busy} />
        {fromQuote ? (
          <>
            <h2 id="remove-supplier-title">Usunąć „{line.name}” tylko ze sprzętówki czy także z kosztorysu?</h2>
            <p className="muted">
              „Tylko ze sprzętówki”: kosztorys i dopożyczenie od {supplierName} zostają, a pozycja nie wróci do sprzętówki przy zmianach kosztorysu. „Ze sprzętówki i kosztorysu”: pozycja znika z kosztorysu, a jej dopożyczenie z „Dopożyczeń”.
            </p>
            {o?.mayDeleteFromQuote && o.quoteNote && <p className="muted">Ze sprzętówki i kosztorysu: {o.quoteNote.charAt(0).toLowerCase() + o.quoteNote.slice(1)}.</p>}
          </>
        ) : (
          <>
            <h2 id="remove-supplier-title">
              Usunąć „{line.name}” i dopożyczenie od {supplierName}?
            </h2>
            {o?.note && <p className="muted">{o.note}.</p>}
          </>
        )}
        {options.isLoading && <p className="muted">Sprawdzam…</p>}
        {(error || options.error) && (
          <p className="alert" role="alert">
            {error || (options.error instanceof ApiError ? options.error.message : 'Nie udało się sprawdzić pozycji')}
          </p>
        )}
        <div className="actions">
          {fromQuote ? (
            <>
              {o?.mayHide && (
                <button className="btn primary" type="button" autoFocus disabled={busy} onClick={() => remove.mutate('packlist')}>
                  Tylko ze sprzętówki
                </button>
              )}
              {o?.mayDeleteFromQuote && (
                <button className="btn" type="button" disabled={busy || !!o.quoteNote} title={o.quoteNote} onClick={() => remove.mutate('quote')}>
                  Ze sprzętówki i kosztorysu
                </button>
              )}
            </>
          ) : (
            <button className="btn primary" type="button" autoFocus disabled={busy || !o?.mayRemove} onClick={() => remove.mutate(null)}>
              {busy ? 'Usuwanie…' : 'Usuń'}
            </button>
          )}
          <button className="btn" type="button" disabled={busy} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Review M2: „Ukryto „X” · Cofnij” after „tylko ze sprzętówki”, as the quote's short notes: it closes by itself after
 * `CREATED_NOTICE_MS` — not while the pointer is over it or „Cofnij” has the focus, nor while „Cofnij” works.
 */
export function HiddenUndo({ eventId, hidden, onClose }: { eventId: string; hidden: HiddenLine; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [held, setHeld] = useState({ pointer: false, focus: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const close = useRef(onClose);
  close.current = onClose;
  const autoHide = !held.pointer && !held.focus && !busy && !error;
  useEffect(() => {
    if (!autoHide) return;
    const t = setTimeout(() => close.current(), CREATED_NOTICE_MS);
    return () => clearTimeout(t);
  }, [autoHide]);
  async function undo() {
    setBusy(true);
    try {
      await api(`/api/quotes/events/${eventId}/packlist-supplier-lines/hidden/${hidden.undoId}/restore`, { method: 'POST', body: {} });
      close.current();
      await refresh(queryClient, eventId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się przywrócić pozycji');
      setBusy(false);
    }
  }
  return (
    <p
      className="pl-undo"
      role="status"
      data-feature-id="packlists.suppliers"
      onPointerEnter={() => setHeld((h) => ({ ...h, pointer: true }))}
      onPointerLeave={() => setHeld((h) => ({ ...h, pointer: false }))}
      onFocus={() => setHeld((h) => ({ ...h, focus: true }))}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHeld((h) => ({ ...h, focus: false }));
      }}
    >
      Ukryto „{hidden.name}”{' · '}
      <button type="button" disabled={busy} title="Przywróć pozycję do sprzętówki" onClick={() => void undo()}>
        Cofnij
      </button>
      {error && <span className="pl-undo-error"> {error}</span>}
    </p>
  );
}
